//Types
import type { AutomationConditionUI } from 'src/types/AutomationConditionUI';
import type { DeviceConditionUI } from 'src/types/DeviceCondAndTriggerUI';
import type { EnergyExchangeConditionUI } from 'src/types/EnergyExchangeConditionUI';
import type { SunriseSunsetConditionUI } from 'src/types/SunriseSunsetConditionUI';
import type { TimeBasedConditionsUI } from 'src/types/TimeBasedConditionsUI';
import type { WeatherConditionUI } from 'src/types/WeatherConditionUI';

import { is_automation_condition_ui } from 'src/types/AutomationConditionUI';
import { is_device_cond_ui } from 'src/types/DeviceCondAndTriggerUI';
import { is_energy_exchange_ui } from 'src/types/EnergyExchangeConditionUI';
import { is_sunrise_sunset_condition_ui } from 'src/types/SunriseSunsetConditionUI';
import { is_time_based_cond } from 'src/types/TimeBasedConditionsUI';
import { is_weather_cond_ui } from 'src/types/WeatherConditionUI';
import {
	_device_condition_msg,
	_energy_exchange_message,
	_math_condition_message,
	_sunrise_sunset_condition_message,
	_time_based_condition_msg,
	_weather_condition_message,
	findIcon,
} from '../helpers/conditions_utils';
import { devices } from 'src/stores/lists/devices';
import { devices_settings } from 'src/stores/device-runtime/status';
import { findImage, findSceneImage } from '../helpers/main_utils';

import { _init_time_based_conditions } from '../../helpers/init_time_conditions';
import { createDeviceID, is_gen_2, real_id } from '../../../../helpers/interface_helpers';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
import { getExtSensorsSettings } from '../../../../helpers/ext_sensor_helpers';
import { scenes } from 'src/stores/lists/scenes';
import { normalizeDeviceChannelForCondition } from '../../helpers/device_channel_condition_helpers';
import { is_math_condition_ui, type MathConditionUI } from 'src/types/Scenes/MathConditionUI';
import { isInvalidWeatherCondition } from 'src/components/Scenes/helpers/has_missing_scene_data';
import { is_manual_execution_condition } from 'src/types/ScenesConditionsTypes';
import type { PresenceConditionUI } from 'src/types/Scenes/PresenceConditionUI';

let _format = get(format);

const PROP_TYPE_TO_GEN2_ADD_ON_TYPE = {
	temperature: 'temp',
	humidity: 'hum',
	vol_meter: 'voltmeter',
	analog_input_add_on: 'analog',
	digital_input_add_on: 'digital',
	push_type_add_on: 'input',
};
// Helper to map device operator
// Previously used to map "equal" to "more than". It was described as "exceptional behaviour for old devices", which does not support "equal" operator.
// Note: the "equal" operator is supported by other devices and thus the mapping was removed.
export const mappedDeviceOperator = (operator: string) => {
	return operator;
};

const mapConditionAndIdToName = (type, addOnId) => {
	switch (type) {
		case 'temperature':
			return `Temperature Sensor (${addOnId})`;
		case 'humidity':
			return `Humidity sensor (${addOnId})`;
		case 'analog_input_add_on':
			return `Analog input (${addOnId})`;
		case 'digital_input_add_on':
			return `Digital input (${addOnId})`;
		case 'push_type_add_on':
			return `Digital button (${addOnId})`;
		case 'vol_meter_add_on':
			return `Voltage meter (${addOnId})`;
		default:
			`${type} (${addOnId})`;
	}
};
/**
 * Because here we only extract props fro device condition, not the actual type trigger or condition
 * we only need to check DeviceConditionType (it is a basic type for trigger also, so it must handle it)
 */
export const extract_conditions_variables = (
	translated:
		| DeviceConditionUI
		| TimeBasedConditionsUI
		| AutomationConditionUI
		| SunriseSunsetConditionUI
		| WeatherConditionUI
		| EnergyExchangeConditionUI
		| MathConditionUI
		| PresenceConditionUI
		| undefined,
) => {
	let title = 'Condition',
		withLabelList = false,
		labelList = [],
		conditionList = [],
		avatar: any = { icon: 'fa fa-question' },
		subtitle = { value: 'Condition Description', bold: false },
		description = { value: '' };
	if (is_device_cond_ui(translated)) {
		const _devices = get(devices);

		const baseId = real_id(translated?.device?.id ?? '');
		const rawChannel = Number(translated?.device?.channel);
		let deviceType = _devices[baseId]?.type;
		if (deviceType === undefined && rawChannel === 1) {
			deviceType = _devices[`${baseId}_1`]?.type;
		}
		let deviceChannel = rawChannel;
		deviceChannel = normalizeDeviceChannelForCondition({
			deviceType,
			deviceChannel,
			condName: translated?.cond_name,
		});

		const device_id = createDeviceID(baseId, deviceChannel, deviceType);

		const device = _devices?.[device_id] ?? _devices?.[baseId];
		const effectiveDeviceId = device?.id ?? device_id;
		const gen = Number(device?.gen);

		withLabelList = true;

		let sensor_name = undefined;
		translated = {
			...translated,
			operator: mappedDeviceOperator(translated.operator),
		};

		if (device && typeof translated.add_info_ind !== undefined && !isNaN(translated?.add_info_ind)) {
			let addOnId = Number(translated.add_info_ind);
			let addOns = [];
			if (is_gen_2(device)) {
				addOns = getAddonStatus(_devices[effectiveDeviceId]);
				//		console.log('GEN @ ADD ONS ', addOns);
				sensor_name = addOns?.find(
					(el) =>
						el?.name === PROP_TYPE_TO_GEN2_ADD_ON_TYPE[translated.cond_name] &&
						el?.sensor?.extid == addOnId,
				)?.sensor?.name;
				// console.log(addOnId, translated.id, sensor_name);
			} else {
				addOns = getExtSensorsSettings(_devices[effectiveDeviceId], translated.cond_name);
				// console.log(' GEN ! ADD ONS ', addOns);
				sensor_name = addOns?.find((sens) => sens.extid == addOnId)?.name || undefined;
			}

			sensor_name = sensor_name
				? sensor_name
				: mapConditionAndIdToName(translated.cond_name, addOnId);
		}

		translated.sensor_name = sensor_name;
		title = translated?.sensor_name
			? `${translated.sensor_name} (${device?.name || ''})`
			: device?.name || _format('missing_device_label', { default: 'Missing Device' });

		let msg = _device_condition_msg(translated, effectiveDeviceId, gen);
		// console.warn('msg', msg);

		subtitle.value = msg.subtitle;
		labelList = [...msg.labelList];
		if (title === _format('missing_device_label', { default: 'Missing Device' })) {
			labelList = [
				{
					value: effectiveDeviceId,
					icon: 'fa-solid fa-microchip',
				},
				...labelList,
			];
		}
		if (translated.cond_name === 'remote_control_channel') {
			labelList = [
				{
					icon: 'fas fa-circle-dot', //ToDo: maybe this icon will be changed to something more appropriate
					value: translated.value,
				},
			];
		}
		if (translated && 'cond_name' in translated && translated.cond_name === 'scroll_position') {
			//THis is for previewing scroll position in scenes
			let labelValue =
				translated.value === 'L'
					? _format('scroll_up', { default: 'Up' })
					: _format('scroll_down', { default: 'Down' });
			let icon = translated.value === 'L' ? 'fas fa-arrow-up' : 'fas fa-arrow-down';
			// Add channel information if available
			if (
				'remote_control_channel' in translated &&
				translated.remote_control_channel &&
				translated.remote_control_channel !== 'any'
			) {
				const channelNum = Number(translated.remote_control_channel.split('_')[1]) + 1;
				labelValue += ` ${_format('label_channel', { values: { channel: channelNum } })}`;
			}
			labelList = [
				{
					icon,
					value: labelValue,
				},
			];
		}

		// Special handling for presence sensor conditions (S4SN-0U61X)
		if (
			deviceType === 'S4SN-0U61X' &&
			(translated.cond_name?.startsWith('prsn_nobj') ||
				translated.cond_name?.startsWith('prsn_st') ||
				translated.cond_name === 'presence_zone_objects_delta' ||
				translated.cond_name === 'presence_zone_objects' ||
				translated.cond_name === 'presence_zone_state')
		) {
			// Extract zone from condition name
			let zoneId = 'Unknown';
			let zoneName = 'Unknown Zone';

			if (translated.cond_name?.startsWith('prsn_nobj_d')) {
				// Delta condition: prsn_nobj_d4202 → scene zone 202 → device zone 201
				const match = translated.cond_name.match(/prsn_nobj_d4(\d{3})/);
				if (match && match[1]) {
					zoneId = (Number(match[1]) - 1).toString();
				}
			} else if (translated.cond_name?.startsWith('prsn_nobj')) {
				const match = translated.cond_name.match(/prsn_nobj4(\d{3})/);
				if (match && match[1]) {
					zoneId = (Number(match[1]) - 1).toString();
				}
			} else if (translated.cond_name?.startsWith('prsn_st')) {
				const match = translated.cond_name.match(/prsn_st4(\d{3})/);
				if (match && match[1]) {
					zoneId = (Number(match[1]) - 1).toString();
				}
			} else if (
				translated.cond_name === 'presence_zone_objects_delta' ||
				translated.cond_name === 'presence_zone_objects' ||
				translated.cond_name === 'presence_zone_state'
			) {
				// Already normalized — add_info_ind holds the device zone ID
				if (translated.add_info_ind !== undefined && translated.add_info_ind !== null) {
					zoneId = String(translated.add_info_ind);
				}
			}

			// Get zone name from device settings using the device zone ID
			const _devices_settings = get(devices_settings);
			const deviceSettingsData = _devices_settings[effectiveDeviceId];
			const zoneKey = `presencezone:${zoneId}`;
			const zoneData = deviceSettingsData?.[zoneKey];

			zoneName = zoneData?.name || `Zone ${zoneId}`;

			const isDelta =
				translated.cond_name?.startsWith('prsn_nobj_d') ||
				translated.cond_name === 'presence_zone_objects_delta';

			if (isDelta) {
				// Enter/leave detection based on operator: more_than = enter, less_than = leave, equal = legacy fallback → enter
				const isEnter = translated.operator === 'more_than' || translated.operator === 'equal';
				const actionLabel = isEnter
					? _format('option_presencezone_enter', { default: 'When enter zone' })
					: _format('option_presencezone_leave', { default: 'When leave zone' });

				labelList = [
					{
						icon: 'fas fa-location-dot',
						value: zoneName,
					},
					{
						icon: isEnter ? 'fas fa-person-walking' : 'fas fa-person',
						value: actionLabel,
					},
				];
				subtitle.value = `${zoneName}: ${actionLabel}`;
			} else if (
				translated.cond_name?.startsWith('prsn_nobj') ||
				translated.cond_name === 'presence_zone_objects'
			) {
				const objectCount = translated.value || 0;
				const objectWord = Number(objectCount) === 1 ? 'object' : 'objects';
				let operatorText = '';

				if (translated.operator === 'gt') {
					operatorText = `Has more than ${objectCount} ${objectWord}`;
				} else if (translated.operator === 'lt') {
					operatorText = `Has less than ${objectCount} ${objectWord}`;
				} else {
					operatorText = `Has ${objectCount} ${objectWord}`;
				}

				labelList = [
					{
						icon: 'fas fa-location-dot',
						value: zoneName,
					},
					{
						icon: 'fas fa-user-group',
						value: operatorText,
					},
				];
				subtitle.value = `${zoneName} ${operatorText.toLowerCase()}`;
			} else if (
				translated.cond_name?.startsWith('prsn_st') ||
				translated.cond_name === 'presence_zone_state'
			) {
				const hasObjects = translated.value === 'true' || translated.value === true;
				const labelText = hasObjects
					? _format('presence_zone_has_objects')
					: _format('presence_zone_has_no_objects');

				labelList = [
					{
						icon: 'fas fa-location-dot',
						value: zoneName,
					},
					{
						icon: hasObjects ? 'fas fa-user-check' : 'fas fa-user-slash',
						value: labelText,
					},
				];
				subtitle.value = `${zoneName} ${labelText.toLowerCase()}`;
			}

			// Override the title to include zone information
			title = `${device?.name || _devices[device_id]?.name || 'Presence Sensor'} - ${zoneName}`;
		}

		conditionList = [...msg.conditionList];

		avatar = findImage(device ?? _devices[device_id]);
		if (avatar.image === undefined && avatar.backgroundColor === undefined) {
			avatar = { icon: device?.icon || 'text-warning fas fa-warning' };
		}
	} else if (is_time_based_cond(translated)) {
		//check time based condition type
		title = _format(_init_time_based_conditions[translated.id].title).toLocaleUpperCase();

		let data = _time_based_condition_msg(translated);
		subtitle.value = data.subtitle;
		description.value = data.description;
		avatar = findIcon(translated);
	} else if (is_automation_condition_ui(translated)) {
		const _scenes = get(scenes);
		let scene = _scenes[translated.scene_id];
		// console.log(_scenes[translated.scene_id]);
		title = scene?._meta?.name || _format('unknown_automation');
		subtitle.value = _format('label_fulfilled');
		avatar = findSceneImage(scene);
	} else if (is_sunrise_sunset_condition_ui(translated)) {
		title = _format('sunrise_sunset_condition_title', {
			default: 'Sunrise/Sunset condition',
		});
		avatar = { icon: 'fal fa-eclipse' };
		// withLabelList = true;
		let data = _sunrise_sunset_condition_message(translated);
		subtitle.value = data.subtitle;
		labelList = data.labelList;
		withLabelList = true;
	} else if (is_weather_cond_ui(translated)) {
		// Weather condition UI
		const data = _weather_condition_message(translated);
		const invalidData = isInvalidWeatherCondition({
			condition: translated.cond,
			value: translated.thsval,
		});
		title = _format('weather_forecast_title', { default: 'Weather forecast' });
		avatar = invalidData
			? { icon: 'text-warning fas fa-warning' }
			: { icon: 'fal fa-cloud-sun-rain' };
		withLabelList = true;
		labelList = data.labelList;
		conditionList = [...data.conditionList];
		subtitle.value = data.subtitle;
	} else if (is_energy_exchange_ui(translated)) {
		title = _format('energy_exchange');
		withLabelList = true;
		let exch_msg = _energy_exchange_message(translated);

		labelList = [...exch_msg.labelList];
		subtitle.value = exch_msg.subtitle;
		title = subtitle.value;
		description.value = exch_msg.description;

		avatar = { icon: 'fal fa-arrow-trend-up' };
	} else if (is_math_condition_ui(translated)) {
		title = _format('title_math_expression', {
			default: 'Math expression',
		});

		withLabelList = true;

		let math_preview = _math_condition_message(translated);
		labelList = [...math_preview.labelList];
		conditionList = [...math_preview.conditionList];

		subtitle.value = math_preview.subtitle;
		description.value = math_preview.description;

		avatar = { icon: 'fal fa-square-root-variable' };
	} else if (is_manual_execution_condition(translated)) {
		title = _format('scenes_title_manual_execution', {
			default: 'Manual Execution',
		});
		subtitle.value = _format('scenes_subtitle_manual_execution', {
			default: 'Can be run manually',
		});
		avatar = { icon: 'fal fa-play-circle' };
	} else {
		title = _format('scenes_title_unknown_condition', {
			default: 'Unknown condition',
		});
		subtitle.value = '';
		labelList = [];
		withLabelList = false;
	}

	return {
		title,
		subtitle,
		avatar,
		description,
		withLabelList,
		labelList,
		conditionList,
	};
};
